import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Table, Badge, Button } from "react-bootstrap";
import api from "../../api/axios";

const metricCard = (title, value, hint) => (
  <Card className="shadow-sm h-100">
    <Card.Body>
      <div className="text-muted small">{title}</div>
      <div className="fs-3 fw-semibold">{value}</div>
      {hint ? <div className="small text-secondary mt-1">{hint}</div> : null}
    </Card.Body>
  </Card>
);

const Dashboard = () => {
  const business = JSON.parse(localStorage.getItem("business") || "null");
  const businessId = business?.id;
  const [subscription, setSubscription] = useState({
    plan_code: business?.plan_code || null,
    billing_status: business?.billing_status || null,
    trial_days_left: business?.trial_days_left ?? null,
    trial_ends_at: business?.trial_ends_at || null,
  });

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [activeClaims, setActiveClaims] = useState([]);
  const [history, setHistory] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);

  const load = async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [productsRes, activeRes, historyRes, reviewsRes] = await Promise.all([
        api.get(`/qr-products/${businessId}`),
        api.get(`/qr/active/${businessId}`),
        api.get(`/qr/history/${businessId}`),
        api.get("/reviews/admin", {
          params: { business_id: businessId, status: "pending", limit: 5, offset: 0 },
        }),
      ]);
      const businessRes = await api.get("/business/me");

      setProducts(productsRes.data || []);
      setActiveClaims(activeRes.data?.claims || []);
      setHistory(historyRes.data?.consumptions || []);
      setPendingReviews(reviewsRes.data?.items || []);
      const b = businessRes.data?.business || {};
      setSubscription({
        plan_code: b.plan_code || null,
        billing_status: b.billing_status || null,
        trial_days_left: b.trial_days_left ?? null,
        trial_ends_at: b.trial_ends_at || null,
      });
    } catch (err) {
      console.error("Error cargando dashboard admin:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [businessId]);

  const metrics = useMemo(() => {
    const activeProducts = products.filter((p) => p.status === "active").length;
    const expiredProducts = products.filter((p) => Number(p.is_expired) === 1).length;

    return {
      totalProducts: products.length,
      activeProducts,
      expiredProducts,
      pendingClaims: activeClaims.length,
      consumptions: history.length,
      pendingReviews: pendingReviews.length,
    };
  }, [products, activeClaims, history, pendingReviews]);

  if (loading) {
    return (
      <Row className="g-3">
        {Array.from({ length: 4 }).map((_, idx) => (
          <Col key={idx} xs={12} md={6} xl={3}>
            <Card className="shadow-sm">
              <Card.Body>
                <div style={{ height: 12, width: "45%", background: "#e5e7eb", borderRadius: 4 }} />
                <div
                  style={{
                    height: 30,
                    width: "30%",
                    background: "#e5e7eb",
                    borderRadius: 4,
                    marginTop: 10,
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Resumen del negocio</h3>
          <small className="text-muted">{business?.name || "Panel QR Benefits"}</small>
        </div>
        <Button variant="outline-secondary" onClick={load}>
          Actualizar
        </Button>
      </div>

      {subscription.billing_status === "trial" && (
        <Card className="mb-3 border-primary shadow-sm">
          <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div className="fw-semibold">Prueba gratuita activa</div>
              <small className="text-muted">
                Plan: {subscription.plan_code || "starter_10"} | Vence:{" "}
                {subscription.trial_ends_at
                  ? new Date(subscription.trial_ends_at).toLocaleDateString()
                  : "-"}
              </small>
            </div>
            <Badge bg={Number(subscription.trial_days_left) <= 7 ? "warning" : "success"} text="dark">
              {Number(subscription.trial_days_left ?? 0)} dias restantes
            </Badge>
          </Card.Body>
        </Card>
      )}

      <Row className="g-3 mb-4">
        <Col xs={12} md={6} xl={3}>
          {metricCard("Beneficios", metrics.totalProducts, `${metrics.activeProducts} activos`)}
        </Col>
        <Col xs={12} md={6} xl={3}>
          {metricCard("Reclamos pendientes", metrics.pendingClaims, "Aun no consumidos")}
        </Col>
        <Col xs={12} md={6} xl={3}>
          {metricCard("Consumos registrados", metrics.consumptions, "Historial total")}
        </Col>
        <Col xs={12} md={6} xl={3}>
          {metricCard("Resenas pendientes", metrics.pendingReviews, `${metrics.expiredProducts} vencidos`)}
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12} xl={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-white fw-semibold">Reclamos activos recientes</Card.Header>
            <Card.Body>
              <Table size="sm" responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Codigo</th>
                    <th>Beneficio</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {activeClaims.slice(0, 6).map((item) => (
                    <tr key={item.id}>
                      <td>{item.claim_code}</td>
                      <td>{item.product?.name || "-"}</td>
                      <td>{new Date(item.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {activeClaims.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-3">
                        No hay reclamos pendientes.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-white fw-semibold">Ultimas resenas pendientes</Card.Header>
            <Card.Body>
              <Table size="sm" responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Rating</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingReviews.slice(0, 6).map((item) => (
                    <tr key={item.id}>
                      <td>{item.display_name || `user:${item.user_id}`}</td>
                      <td>
                        <Badge bg="warning" text="dark">
                          {item.rating}/5
                        </Badge>
                      </td>
                      <td>{new Date(item.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {pendingReviews.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-3">
                        No hay resenas pendientes.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
